import React, { useEffect, useState } from "react";
import { Autocomplete, Chip, Stack, TextField } from "@mui/material";
import {
  useGetAwardFilterOptionsQuery,
  useLazyGetOrganizationOptionsQuery,
} from "../../services/rtkQueryServices/checkApiService";
import useAwardSearch from "./useAwardSearch";
import FacetSelect, { FACET_LABELS } from "./FacetSelect";

// Advanced facets for the Awards tab: Category / Level / Kind (from
// /award-filter-options) + an Organization typeahead. The Awardees tab's facets
// (Academy, Active in year) now sit inline in the search shell, so this panel is
// awards-only. Every change writes straight to the URL; active facets show as
// removable chips.
const asOptions = (list) => (list || []).map((v) => ({ value: v, label: v }));

export default function AdvancedFilters() {
  const { params, setParams } = useAwardSearch();
  const { data: options } = useGetAwardFilterOptionsQuery();
  const [fetchOrgs, { data: orgData, isFetching: orgLoading }] =
    useLazyGetOrganizationOptionsQuery();
  const [orgInput, setOrgInput] = useState(params.org || "");

  // Debounced organization typeahead.
  useEffect(() => {
    const term = orgInput.trim();
    if (term.length < 2) return;
    const t = setTimeout(() => fetchOrgs(term), 300);
    return () => clearTimeout(t);
  }, [orgInput, fetchOrgs]);

  // Keep the org input in sync if the facet is cleared externally (e.g. on a
  // tab switch), so a stale typed value doesn't linger in the box.
  useEffect(() => {
    setOrgInput(params.org || "");
  }, [params.org]);

  const setFacet = (key, value) => setParams({ [key]: value || "", page: 1 });

  const activeFacets = ["cat", "level", "kind", "org"].filter((k) => params[k]);

  return (
    <div className="awards-advanced">
      <Stack
        direction="row"
        spacing={1.5}
        flexWrap="wrap"
        rowGap={1.5}
        alignItems="center"
      >
        <FacetSelect facet="cat" options={asOptions(options?.category)} minWidth={170} />
        <FacetSelect facet="level" options={asOptions(options?.level)} minWidth={170} />
        <FacetSelect facet="kind" options={asOptions(options?.kind)} minWidth={170} />
        <Autocomplete
          freeSolo
          size="small"
          sx={{ minWidth: 240 }}
          options={orgData?.data || []}
          loading={orgLoading}
          value={params.org || null}
          inputValue={orgInput}
          onInputChange={(_, v) => setOrgInput(v)}
          onChange={(_, v) => setFacet("org", v || "")}
          renderInput={(p) => <TextField {...p} placeholder="Organization" />}
        />
      </Stack>

      {activeFacets.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          rowGap={1}
          sx={{ mt: 1.5 }}
        >
          {activeFacets.map((k) => (
            <Chip
              key={k}
              size="small"
              color="primary"
              variant="outlined"
              label={`${FACET_LABELS[k]}: ${params[k]}`}
              onDelete={() => {
                if (k === "org") setOrgInput("");
                setFacet(k, "");
              }}
            />
          ))}
        </Stack>
      )}
    </div>
  );
}
